import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import captureScreenshot from 'utils/screenshotUtils';
import {
  AttachMoney,
  CompareArrows,
  Payments,
  CheckCircle,
  Cancel,
  TrendingUp,
  ChevronRight,
  LocalOffer,
  Warning
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Favicon from 'components/Favicon';

// Get nested property from object safely
const getNestedProperty = (obj, path) => {
  if (!obj) return null;

  const properties = path.split('.');
  let current = obj;

  for (const property of properties) {
    if (current === null || current === undefined) {
      return null;
    }
    current = current[property];
  }

  return current;
};

// Get a color for each competitor
const getCompetitorColor = (index) => {
  const colors = [
    '#3f51b5', // primary
    '#f44336', // red
    '#4caf50', // green
    '#ff9800', // orange
    '#2196f3', // blue
    '#9c27b0', // purple
    '#00bcd4', // cyan
    '#ff5722', // deep orange
    '#673ab7', // deep purple
    '#795548' // brown
  ];
  return colors[index % colors.length];
};

// Helper to render feature values with consistent styling
const renderFeatureValue = (value, available = true) => {
  if (!available) return <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>N/A</Typography>;

  if (value === 'Unlimited') {
    return <Typography sx={{ color: 'success.main', fontWeight: 600 }}>{value}</Typography>;
  } else if (value === 'unknown' || value === null || value === undefined) {
    return <Typography sx={{ color: 'warning.main' }}>-</Typography>;
  } else if (value === true || value === 'Yes') {
    return <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />;
  } else if (value === false || value === 'No') {
    return <Cancel sx={{ color: 'error.main', fontSize: 18 }} />;
  } else {
    return <Typography>{value}</Typography>;
  }
};

const CompaniesPricingAnalysis = ({ competitors, companyData }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const open = Boolean(anchorEl);
  const contentRef = useRef(null);

  // Filter competitors to only include those with pricing analysis data
  const filteredCompetitors = competitors.filter((competitor) => {
    const pricingAnalysis = getNestedProperty(competitor, 'analysis_data.pricing_analysis');
    return pricingAnalysis && pricingAnalysis.has_pricing_info;
  });

  // Check if company data has pricing analysis
  const hasCompanyPricingAnalysis =
    companyData &&
    companyData.analysis_data &&
    companyData.analysis_data.pricing_analysis &&
    companyData.analysis_data.pricing_analysis.has_pricing_info;

  // Get the main company name - use service_name if available
  const mainCompanyName = companyData?.service_name || companyData?.company_name || 'My Company';

  // Combine company data with competitors if it has pricing analysis
  const allCompanies = hasCompanyPricingAnalysis
    ? [{ ...companyData, isMainCompany: true }, ...filteredCompetitors]
    : [...filteredCompetitors];

  // Handle menu
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Export as PNG
  const downloadAsPng = () => {
    captureScreenshot({
      contentRef,
      filename: 'pricing-analysis-comparison',
      afterCapture: handleClose
    });
  };

  // If no companies with pricing analysis data, return null
  if (allCompanies.length === 0) return null;

  // Render pricing overview comparison table
  const renderPricingOverviewTable = () => {
    return (
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          mb: 4
        }}
        id="pricing-pricing_model"
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'background.neutral' }}>
              <TableCell sx={{ fontWeight: 600, width: '20%' }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Pricing Model</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Price Range</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Billing Options</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Free Plan</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Trial</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allCompanies.map((company, index) => {
              const pricingAnalysis = getNestedProperty(company, 'analysis_data.pricing_analysis');
              const priceRange = pricingAnalysis?.price_range || {};
              const freePlan = pricingAnalysis?.free_plan || {};
              const trialOffering = pricingAnalysis?.trial_offering || {};
              const isMainCompany = company.isMainCompany;
              const companyColor = isMainCompany ? 'primary.main' : getCompetitorColor(index - (hasCompanyPricingAnalysis ? 1 : 0));
              const displayName = isMainCompany ? mainCompanyName : company.company_name;

              return (
                <TableRow
                  key={displayName}
                  sx={{
                    bgcolor: isMainCompany ? 'primary.lighter' : index % 2 === 0 ? 'background.paper' : 'background.neutral'
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {isMainCompany ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 18,
                            height: 18,
                            bgcolor: 'primary.main',
                            borderRadius: '50%',
                            mr: 1.5,
                            color: 'white',
                            fontSize: '0.7rem',
                            fontWeight: 'bold'
                          }}
                        >
                          ★
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            bgcolor: companyColor,
                            borderRadius: '50%',
                            mr: 1.5
                          }}
                        />
                      )}
                      <Favicon company={displayName} size={20} />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: isMainCompany ? 700 : 600,
                          color: isMainCompany ? 'primary.dark' : 'text.primary',
                          ml: 1
                        }}
                      >
                        {displayName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{renderFeatureValue(pricingAnalysis?.pricing_model, !!pricingAnalysis)}</TableCell>
                  <TableCell>
                    {pricingAnalysis && priceRange.lowest && priceRange.highest ? (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: companyColor,
                          color: 'text.primary'
                        }}
                      >
                        <AttachMoney sx={{ fontSize: 16, color: companyColor, mr: 0.5 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {`${priceRange.lowest} - ${priceRange.highest} ${priceRange.currency || ''}`}
                        </Typography>
                      </Box>
                    ) : (
                      renderFeatureValue(null, !!pricingAnalysis)
                    )}
                  </TableCell>
                  <TableCell>
                    {pricingAnalysis && pricingAnalysis.billing_options && pricingAnalysis.billing_options.length > 0 ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {pricingAnalysis.billing_options.map((option, i) => (
                          <Chip
                            key={i}
                            label={option}
                            size="small"
                            sx={{
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                              mb: 0.5
                            }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      renderFeatureValue(null, !!pricingAnalysis)
                    )}
                  </TableCell>
                  <TableCell>
                    {pricingAnalysis ? (
                      freePlan.available ? (
                        <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
                      ) : (
                        <Cancel sx={{ color: 'error.main', fontSize: 18 }} />
                      )
                    ) : (
                      renderFeatureValue(null, false)
                    )}
                  </TableCell>
                  <TableCell>
                    {pricingAnalysis ? (
                      trialOffering.available ? (
                        <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
                      ) : (
                        <Cancel sx={{ color: 'error.main', fontSize: 18 }} />
                      )
                    ) : (
                      renderFeatureValue(null, false)
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render pricing strategy comparison
  const renderPricingStrategyComparison = () => {
    return (
      <Box sx={{ mb: 4 }} id="pricing-pricing_strategy">
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
          Pricing Strategy Comparison
        </Typography>

        <Grid container spacing={3}>
          {allCompanies.map((company, index) => {
            const pricingAnalysis = getNestedProperty(company, 'analysis_data.pricing_analysis');
            const isMainCompany = company.isMainCompany;
            const companyColor = isMainCompany ? 'primary.main' : getCompetitorColor(index - (hasCompanyPricingAnalysis ? 1 : 0));
            const displayName = isMainCompany ? mainCompanyName : company.company_name;

            if (!pricingAnalysis || !pricingAnalysis.pricing_strategy) return null;

            return (
              <Grid item xs={12} md={6} key={displayName}>
                <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                  <Box
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 2,
                      bgcolor: isMainCompany ? 'primary.lighter' : 'background.paper',
                      position: 'relative',
                      isolation: 'isolate',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        padding: '1px',
                        borderRadius: 'inherit',
                        background: `linear-gradient(135deg, ${companyColor}80 0%, ${companyColor}40 60%, ${companyColor}20 100%)`,
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        zIndex: -1
                      },
                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                      }
                    }}
                  >
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {isMainCompany ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 24,
                              height: 24,
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              color: 'white',
                              fontSize: '0.8rem',
                              fontWeight: 'bold'
                            }}
                          >
                            ★
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: companyColor,
                              borderRadius: '50%'
                            }}
                          />
                        )}
                        <Favicon company={displayName} size={20} />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: isMainCompany ? 'primary.dark' : 'text.primary',
                            ml: 1
                          }}
                        >
                          {displayName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <TrendingUp sx={{ color: companyColor, mt: 0.5 }} />
                        <Typography variant="body1">{pricingAnalysis.pricing_strategy}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // Render pricing conclusion comparison
  const renderPricingConclusionComparison = () => {
    return (
      <Box id="pricing-pricing_conclusion">
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
          Pricing Evaluation & Recommendations
        </Typography>

        <Grid container spacing={3}>
          {allCompanies.map((company, index) => {
            const pricingAnalysis = getNestedProperty(company, 'analysis_data.pricing_analysis');
            const pricingConclusion = pricingAnalysis?.pricing_conclusion || {};
            const isMainCompany = company.isMainCompany;
            const companyColor = isMainCompany ? 'primary.main' : getCompetitorColor(index - (hasCompanyPricingAnalysis ? 1 : 0));
            const displayName = isMainCompany ? mainCompanyName : company.company_name;

            if (!pricingAnalysis || !pricingConclusion.summary) return null;

            return (
              <Grid item xs={12} key={displayName}>
                <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.2 }}>
                  <Box
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 2,
                      bgcolor: isMainCompany ? 'primary.lighter' : 'background.paper',
                      position: 'relative',
                      isolation: 'isolate',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        padding: '1px',
                        borderRadius: 'inherit',
                        background: `linear-gradient(135deg, ${companyColor}80 0%, ${companyColor}40 60%, ${companyColor}20 100%)`,
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        zIndex: -1
                      },
                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                      },
                      mb: 2
                    }}
                  >
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {isMainCompany ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 24,
                              height: 24,
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              color: 'white',
                              fontSize: '0.8rem',
                              fontWeight: 'bold'
                            }}
                          >
                            ★
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: companyColor,
                              borderRadius: '50%'
                            }}
                          />
                        )}
                        <Favicon company={displayName} size={20} />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: isMainCompany ? 'primary.dark' : 'text.primary',
                            ml: 1
                          }}
                        >
                          {displayName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <ChevronRight sx={{ color: companyColor, mt: 0.5 }} />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {pricingConclusion.summary}
                        </Typography>
                      </Box>
                      {pricingConclusion.recommendations && pricingConclusion.recommendations.length > 0 && (
                        <Box sx={{ ml: 4 }}>
                          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                            Recommendations:
                          </Typography>
                          <Stack spacing={1}>
                            {pricingConclusion.recommendations.map((rec, i) => (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <ChevronRight sx={{ color: companyColor, fontSize: 18, mt: 0.3 }} />
                                <Typography variant="body2">{rec}</Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        width: '100%',
        mb: 3,
        borderRadius: 2,
        position: 'relative',
        isolation: 'isolate',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          padding: '1px',
          borderRadius: 'inherit',
          background: 'linear-gradient(135deg, rgba(63,81,181,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          zIndex: -1
        }
      }}
      ref={contentRef}
      id="section-pricing"
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Pricing Analysis
          </Typography>

          <IconButton
            onClick={handleClick}
            size="small"
            sx={{
              height: 28,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
            aria-label="download options"
            className="screenshot-controls"
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            className="screenshot-controls"
            slotProps={{
              paper: {
                sx: {
                  border: 1,
                  borderColor: 'secondary.light',
                  borderRadius: 1
                }
              }
            }}
          >
            <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
          </Menu>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Company tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" aria-label="companies pricing tabs">
            {/* All Pricing tab */}
            <Tab
              key="all-pricing"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CompareArrows sx={{ mr: 1, fontSize: 20 }} />
                  <Typography>Comparison</Typography>
                </Box>
              }
            />

            {/* Individual company tabs */}
            {allCompanies.map((company, index) => (
              <Tab
                key={company.company_name || mainCompanyName}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {company.isMainCompany ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          bgcolor: 'primary.main',
                          borderRadius: '50%',
                          mr: 1,
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 'bold'
                        }}
                      >
                        ★
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          bgcolor: getCompetitorColor(index - (hasCompanyPricingAnalysis ? 1 : 0)),
                          borderRadius: '50%',
                          mr: 1
                        }}
                      />
                    )}
                    <Favicon company={company.isMainCompany ? mainCompanyName : company.company_name} size={20} />
                    <Typography sx={{ ml: 1 }}>{company.isMainCompany ? mainCompanyName : company.company_name}</Typography>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab content */}
        {currentTab === 0 ? (
          // Comparison Tab
          <Box>
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <LocalOffer sx={{ color: 'primary.main' }} />
                <Typography variant="h6">Pricing Overview</Typography>
              </Stack>
              {renderPricingOverviewTable()}
            </Box>

            {renderPricingStrategyComparison()}

            {renderPricingConclusionComparison()}
          </Box>
        ) : (
          // Individual company tabs - placeholder for now
          <Box>
            {allCompanies.map((company, companyIndex) => {
              const tabIndex = companyIndex + 1; // Add 1 to account for the comparison tab
              if (currentTab !== tabIndex) return null;

              const pricingAnalysis = getNestedProperty(company, 'analysis_data.pricing_analysis');
              if (!pricingAnalysis) {
                return (
                  <Box sx={{ p: 3, textAlign: 'center' }} key={`company-${companyIndex}`}>
                    <Typography variant="body1" color="text.secondary">
                      No pricing analysis data available for this company.
                    </Typography>
                  </Box>
                );
              }

              const isMainCompany = company.isMainCompany;
              const companyColor = isMainCompany ? 'primary.main' : getCompetitorColor(companyIndex - (hasCompanyPricingAnalysis ? 1 : 0));
              const displayName = isMainCompany ? mainCompanyName : company.company_name;

              return (
                <Box key={`company-${companyIndex}`}>
                  {/* Basic Information Card */}
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <Card
                      sx={{
                        mb: 4,
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 3,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                      }}
                    >
                      {/* Gradient overlay */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `linear-gradient(135deg, ${companyColor}10 0%, ${companyColor}10 50%, ${companyColor}00 100%)`,
                          zIndex: 0
                        }}
                      />

                      <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
                        <Stack spacing={3}>
                          {/* Section Header */}
                          <Box sx={{ mb: 0 }}>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700,
                                mb: 1,
                                color: companyColor
                              }}
                            >
                              Pricing Overview: {displayName}
                            </Typography>
                          </Box>

                          {/* Content Stack */}
                          <Stack spacing={3}>
                            {/* Basic Information */}
                            <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                              <Box
                                sx={{
                                  p: 3,
                                  height: '100%',
                                  borderRadius: 2,
                                  bgcolor: 'background.paper',
                                  position: 'relative',
                                  isolation: 'isolate',
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    padding: '1px',
                                    borderRadius: 'inherit',
                                    background: `linear-gradient(135deg, ${companyColor}80 0%, ${companyColor}60 60%, ${companyColor}20 100%)`,
                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    WebkitMaskComposite: 'xor',
                                    maskComposite: 'exclude',
                                    zIndex: -1
                                  },
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                                  }
                                }}
                              >
                                <Stack spacing={2}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 1.5
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5
                                      }}
                                    >
                                      <Box sx={{ color: companyColor }}>
                                        <LocalOffer />
                                      </Box>
                                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Basic Information
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: 'text.secondary',
                                          fontWeight: 500
                                        }}
                                      >
                                        Price Range:
                                      </Typography>
                                      {pricingAnalysis.price_range && (
                                        <Box
                                          sx={{
                                            px: 2,
                                            py: 0.75,
                                            borderRadius: 2,
                                            background: `linear-gradient(135deg, ${companyColor}20 0%, ${companyColor}20 100%)`,
                                            border: '1px solid',
                                            borderColor: companyColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                          }}
                                        >
                                          <Typography
                                            variant="h6"
                                            sx={{
                                              fontWeight: 700,
                                              color: companyColor
                                            }}
                                          >
                                            {`${pricingAnalysis.price_range.lowest} - ${pricingAnalysis.price_range.highest} ${
                                              pricingAnalysis.price_range.currency || ''
                                            }`}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <ChevronRight sx={{ color: companyColor, fontSize: 20, mt: 0.5 }} />
                                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                      <Box component="span" sx={{ fontWeight: 600 }}>
                                        Pricing Model:
                                      </Box>{' '}
                                      {pricingAnalysis.pricing_model || 'Not specified'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <ChevronRight sx={{ color: companyColor, fontSize: 20, mt: 0.5 }} />
                                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                      <Box component="span" sx={{ fontWeight: 600 }}>
                                        Billing Options:
                                      </Box>{' '}
                                      {pricingAnalysis.billing_options?.join(', ') || 'Not specified'}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Box>
                            </motion.div>

                            {/* Plans & Trials */}
                            <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                              <Box
                                sx={{
                                  p: 3,
                                  height: '100%',
                                  borderRadius: 2,
                                  bgcolor: 'background.paper',
                                  position: 'relative',
                                  isolation: 'isolate',
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    padding: '1px',
                                    borderRadius: 'inherit',
                                    background: `linear-gradient(135deg, ${companyColor}80 0%, ${companyColor}60 60%, ${companyColor}20 100%)`,
                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    WebkitMaskComposite: 'xor',
                                    maskComposite: 'exclude',
                                    zIndex: -1
                                  },
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                                  }
                                }}
                              >
                                <Stack spacing={2}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ color: companyColor }}>
                                      <Payments />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                      Plans & Trials
                                    </Typography>
                                  </Box>
                                  {pricingAnalysis.free_plan && pricingAnalysis.free_plan.available && (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                      <ChevronRight sx={{ color: companyColor, fontSize: 20, mt: 0.5 }} />
                                      <Box sx={{ position: 'relative', flex: 1 }}>
                                        <Typography component="span" sx={{ fontWeight: 600 }}>
                                          Free Plan:{' '}
                                        </Typography>
                                        <Typography component="span">
                                          {pricingAnalysis.free_plan.details || 'Details not available'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}
                                  {pricingAnalysis.trial_offering && pricingAnalysis.trial_offering.available && (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                      <ChevronRight sx={{ color: companyColor, fontSize: 20, mt: 0.5 }} />
                                      <Box sx={{ position: 'relative', flex: 1 }}>
                                        <Typography component="span" sx={{ fontWeight: 600 }}>
                                          Trial Offering:{' '}
                                        </Typography>
                                        <Typography component="span">
                                          {pricingAnalysis.trial_offering.details || 'Details not available'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}
                                </Stack>
                              </Box>
                            </motion.div>

                            {/* Pricing Strategy */}
                            <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                              <Box
                                sx={{
                                  p: 3,
                                  height: '100%',
                                  borderRadius: 2,
                                  bgcolor: 'background.paper',
                                  position: 'relative',
                                  isolation: 'isolate',
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    padding: '1px',
                                    borderRadius: 'inherit',
                                    background: `linear-gradient(135deg, ${companyColor}80 0%, ${companyColor}60 60%, ${companyColor}20 100%)`,
                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    WebkitMaskComposite: 'xor',
                                    maskComposite: 'exclude',
                                    zIndex: -1
                                  },
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                                  }
                                }}
                              >
                                <Stack spacing={2}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        color: companyColor,
                                        display: 'flex',
                                        alignItems: 'center'
                                      }}
                                    >
                                      <TrendingUp />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                      Pricing Strategy
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: 1
                                    }}
                                  >
                                    <ChevronRight
                                      sx={{
                                        color: companyColor,
                                        fontSize: 20,
                                        mt: 0.5
                                      }}
                                    />
                                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                      {pricingAnalysis.pricing_strategy}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Box>
                            </motion.div>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Pricing Plans */}
                  {pricingAnalysis.pricing_plans && pricingAnalysis.pricing_plans.length > 0 && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          mb: 3,
                          color: companyColor
                        }}
                      >
                        Plans: {displayName}
                      </Typography>

                      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
                        {pricingAnalysis.pricing_plans.map((plan, index) => (
                          <Grid item xs={12} md={6} key={index}>
                            <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                              <Box
                                sx={{
                                  p: { xs: 2, md: 4 },
                                  height: '100%',
                                  borderRadius: 4,
                                  bgcolor: index % 2 === 0 ? `${companyColor}10` : `${companyColor}15`,
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                                  }
                                }}
                              >
                                <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                                  {plan.plan_name}
                                </Typography>
                                <Typography variant="h3" sx={{ mb: 3, fontWeight: 800 }}>
                                  {plan.price}
                                  <Typography component="span" variant="body1" sx={{ ml: 1, color: 'text.secondary' }}>
                                    /{plan.billing_frequency}
                                  </Typography>
                                </Typography>
                                <Stack spacing={2}>
                                  {plan.key_features.map((feature, idx) => (
                                    <Stack direction="row" spacing={1} alignItems="flex-start" key={idx}>
                                      <CheckCircle sx={{ color: 'success.main', fontSize: 20, mt: 0.3 }} />
                                      <Typography variant="body2" color="text.secondary">
                                        {feature}
                                      </Typography>
                                    </Stack>
                                  ))}
                                </Stack>
                              </Box>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>
                    </motion.div>
                  )}

                  {/* Plan Analysis */}
                  {pricingAnalysis.plan_comparison_analysis &&
                    pricingAnalysis.plan_comparison_analysis.plans &&
                    pricingAnalysis.plan_comparison_analysis.plans.length > 0 && (
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.6 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            mb: 3,
                            color: companyColor
                          }}
                        >
                          Plans Analysis
                        </Typography>
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                          {pricingAnalysis.plan_comparison_analysis.plans.map((plan, index) => (
                            <Grid item xs={12} md={6} key={index}>
                              <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                                <Box
                                  sx={{
                                    p: 3,
                                    height: '100%',
                                    borderRadius: 4,
                                    bgcolor: index % 2 === 0 ? `${companyColor}10` : `${companyColor}15`,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                                    }
                                  }}
                                >
                                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                                    {plan.plan_name}
                                  </Typography>
                                  <Stack spacing={2}>
                                    <Box>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Use Cases
                                      </Typography>
                                      <Typography variant="body2">{plan.use_cases}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Target Users
                                      </Typography>
                                      <Typography variant="body2">{plan.target_users}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Value Proposition
                                      </Typography>
                                      <Typography variant="body2">{plan.value_proposition}</Typography>
                                    </Box>
                                  </Stack>
                                </Box>
                              </motion.div>
                            </Grid>
                          ))}
                        </Grid>
                      </motion.div>
                    )}

                  {/* Features Comparison */}
                  {pricingAnalysis.features_comparison && Object.keys(pricingAnalysis.features_comparison).length > 0 && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.7 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          mb: 3,
                          color: companyColor
                        }}
                      >
                        Features Comparison
                      </Typography>
                      <Box
                        sx={{
                          width: '100%',
                          overflowX: 'auto',
                          maxWidth: '100%',
                          display: 'block',
                          mb: 4
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: { xs: '100%', sm: '100%', md: '800px' },
                            width: '100%'
                          }}
                        >
                          <Table sx={{ tableLayout: 'fixed' }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Feature</TableCell>
                                {pricingAnalysis.pricing_plans &&
                                  pricingAnalysis.pricing_plans.map((plan, index) => (
                                    <TableCell key={index} align="center" sx={{ fontWeight: 700 }}>
                                      {plan.plan_name}
                                    </TableCell>
                                  ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(pricingAnalysis.features_comparison).map(([feature, values], index) => (
                                <TableRow
                                  key={index}
                                  sx={{
                                    '&:nth-of-type(odd)': { bgcolor: 'background.default' }
                                  }}
                                >
                                  <TableCell>
                                    <Typography>{feature}</Typography>
                                  </TableCell>
                                  {values.map((value, idx) => (
                                    <TableCell key={idx} align="center">
                                      {renderFeatureValue(value)}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Box>
                    </motion.div>
                  )}

                  {/* Conclusion & Recommendations */}
                  {pricingAnalysis.pricing_conclusion &&
                    (pricingAnalysis.pricing_conclusion.summary ||
                      (pricingAnalysis.pricing_weaknesses && pricingAnalysis.pricing_weaknesses.length > 0)) && (
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.8 }}>
                        <Card
                          sx={{
                            mb: 4,
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: 3,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                          }}
                        >
                          {/* Gradient overlay */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: `linear-gradient(135deg, ${companyColor}10 0%, ${companyColor}10 50%, ${companyColor}00 100%)`,
                              zIndex: 0
                            }}
                          />

                          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                            <Stack spacing={3}>
                              {/* Section Header */}
                              <Box sx={{ mb: 0 }}>
                                <Typography
                                  variant="h5"
                                  sx={{
                                    fontWeight: 700,
                                    mb: 1,
                                    color: companyColor
                                  }}
                                >
                                  Evaluation and Recommendations
                                </Typography>
                              </Box>

                              {/* Content Stack */}
                              <Stack spacing={3}>
                                {/* Conclusion */}
                                {pricingAnalysis.pricing_conclusion && pricingAnalysis.pricing_conclusion.summary && (
                                  <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                                    <Box
                                      sx={{
                                        p: 3,
                                        height: '100%',
                                        borderRadius: 2,
                                        bgcolor: 'background.paper',
                                        position: 'relative',
                                        isolation: 'isolate',
                                        '&::before': {
                                          content: '""',
                                          position: 'absolute',
                                          inset: 0,
                                          padding: '1px',
                                          borderRadius: 'inherit',
                                          background: `linear-gradient(135deg, ${companyColor}80 0%, ${companyColor}40 60%, ${companyColor}20 100%)`,
                                          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                          WebkitMaskComposite: 'xor',
                                          maskComposite: 'exclude',
                                          zIndex: -1
                                        },
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                                        }
                                      }}
                                    >
                                      <Stack spacing={2}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                          <Box sx={{ color: companyColor }}>
                                            <CheckCircle />
                                          </Box>
                                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Conclusion
                                          </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                          <ChevronRight sx={{ color: companyColor, fontSize: 20, mt: 0.3 }} />
                                          <Typography variant="body1">{pricingAnalysis.pricing_conclusion.summary}</Typography>
                                        </Box>
                                        {pricingAnalysis.pricing_conclusion.recommendations &&
                                          pricingAnalysis.pricing_conclusion.recommendations.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                                Recommendations:
                                              </Typography>
                                              <Stack spacing={1}>
                                                {pricingAnalysis.pricing_conclusion.recommendations.map((rec, index) => (
                                                  <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                                                    <ChevronRight sx={{ color: companyColor, fontSize: 20, mt: 0.3 }} />
                                                    <Typography variant="body2">{rec}</Typography>
                                                  </Box>
                                                ))}
                                              </Stack>
                                            </Box>
                                          )}
                                      </Stack>
                                    </Box>
                                  </motion.div>
                                )}

                                {/* Areas for Improvement */}
                                {pricingAnalysis.pricing_weaknesses && pricingAnalysis.pricing_weaknesses.length > 0 && (
                                  <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                                    <Box
                                      sx={{
                                        p: 3,
                                        height: '100%',
                                        borderRadius: 2,
                                        bgcolor: 'background.paper',
                                        position: 'relative',
                                        isolation: 'isolate',
                                        '&::before': {
                                          content: '""',
                                          position: 'absolute',
                                          inset: 0,
                                          padding: '1px',
                                          borderRadius: 'inherit',
                                          background:
                                            'linear-gradient(135deg, rgba(255,87,34,0.5) 0%, rgba(255,152,0,0.5) 60%, rgba(255,152,0,0.2) 100%)',
                                          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                          WebkitMaskComposite: 'xor',
                                          maskComposite: 'exclude',
                                          zIndex: -1
                                        },
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                                        }
                                      }}
                                    >
                                      <Stack spacing={2}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                          <Box sx={{ color: 'warning.main' }}>
                                            <Warning />
                                          </Box>
                                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Areas for Improvement
                                          </Typography>
                                        </Box>
                                        <Stack spacing={1}>
                                          {pricingAnalysis.pricing_weaknesses.map((weakness, index) => (
                                            <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                                              <ChevronRight sx={{ color: 'warning.main', fontSize: 20, mt: 0.3 }} />
                                              <Typography variant="body2">{weakness}</Typography>
                                            </Box>
                                          ))}
                                        </Stack>
                                      </Stack>
                                    </Box>
                                  </motion.div>
                                )}
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      </motion.div>
                    )}
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CompaniesPricingAnalysis;
